import { Badge, humanise, type Tone } from '@/features/transport/shared';
import type {
  AssetAuditStatus,
  AssetCondition,
  AssetEventType,
  AssetRecordStatus,
  AssetStatus,
  LocationKind,
  StockMovementType,
  WarrantyState,
} from '@/types/asset';

/*
 * Pieces every asset screen uses.
 *
 * The pill, the detail row, the headline figure and the money cell are the
 * transport module's, re-exported rather than copied, so the two modules
 * cannot drift apart visually. The maps below are exhaustive over their union
 * types on purpose — a new state on the server becomes a compile error here
 * rather than a blank badge.
 */

export { Badge, BarRow, Field, Money, Stat, humanise, today } from '@/features/transport/shared';
export type { Tone } from '@/features/transport/shared';

/**
 * LOST is the danger: the school cannot say where the thing is. DISPOSED is
 * neutral — final, but deliberate and on the record.
 */
export const ASSET_STATUS_TONE: Record<AssetStatus, Tone> = {
  IN_STOCK: 'info',
  IN_USE: 'success',
  UNDER_MAINTENANCE: 'warning',
  LOST: 'danger',
  DISPOSED: 'neutral',
};

export function AssetStatusBadge({ status }: { status: AssetStatus }) {
  return <Badge tone={ASSET_STATUS_TONE[status]}>{humanise(status)}</Badge>;
}

export const ASSET_CONDITION_TONE: Record<AssetCondition, Tone> = {
  NEW: 'success',
  GOOD: 'success',
  FAIR: 'info',
  POOR: 'warning',
  DAMAGED: 'danger',
};

export function AssetConditionBadge({ condition }: { condition: AssetCondition }) {
  return <Badge tone={ASSET_CONDITION_TONE[condition]}>{humanise(condition)}</Badge>;
}

export const AUDIT_STATUS_TONE: Record<AssetAuditStatus, Tone> = {
  OPEN: 'info',
  CLOSED: 'neutral',
};

export function AuditStatusBadge({ status }: { status: AssetAuditStatus }) {
  return <Badge tone={AUDIT_STATUS_TONE[status]}>{humanise(status)}</Badge>;
}

export const RECORD_STATUS_TONE: Record<AssetRecordStatus, Tone> = {
  ACTIVE: 'success',
  INACTIVE: 'neutral',
};

/** Categories, locations and stock items: in use or retired. */
export function RecordStatusBadge({ status }: { status: AssetRecordStatus }) {
  return <Badge tone={RECORD_STATUS_TONE[status]}>{humanise(status)}</Badge>;
}

/** The server's verdict (EXPIRING = within 30 days), never recomputed here. */
export const WARRANTY_TONE: Record<WarrantyState, Tone> = {
  NONE: 'neutral',
  VALID: 'success',
  EXPIRING: 'warning',
  EXPIRED: 'danger',
};

export const WARRANTY_LABEL: Record<WarrantyState, string> = {
  NONE: 'no warranty',
  VALID: 'under warranty',
  EXPIRING: 'warranty expiring',
  EXPIRED: 'warranty expired',
};

export function WarrantyBadge({ state }: { state: WarrantyState }) {
  return <Badge tone={WARRANTY_TONE[state]}>{WARRANTY_LABEL[state]}</Badge>;
}

/**
 * A count line's result. An unchecked line says so out loud — it is never
 * presumed found.
 */
export function AuditLineResultBadge({ found }: { found?: boolean }) {
  if (found !== true && found !== false) {
    return <Badge tone="neutral">unchecked</Badge>;
  }
  return found ? <Badge tone="success">found</Badge> : <Badge tone="danger">missing</Badge>;
}

/** Uses the server's `belowReorderLevel` flag; no arithmetic here. */
export function StockLevelBadge({ belowReorderLevel }: { belowReorderLevel: boolean }) {
  return belowReorderLevel ? (
    <Badge tone="warning">reorder</Badge>
  ) : (
    <Badge tone="success">ok</Badge>
  );
}

/** How each history entry reads on the timeline. */
export const ASSET_EVENT_LABEL: Record<AssetEventType, string> = {
  REGISTERED: 'Registered',
  ASSIGNED: 'Assigned',
  UNASSIGNED: 'Returned to stock',
  MOVED: 'Moved',
  CONDITION_CHANGED: 'Condition changed',
  MAINTENANCE_STARTED: 'Sent for maintenance',
  MAINTENANCE_COMPLETED: 'Back from maintenance',
  REPORTED_LOST: 'Reported lost',
  FOUND: 'Found',
  DISPOSED: 'Disposed',
  AUDITED: 'Counted',
};

export const ASSET_EVENT_TONE: Record<AssetEventType, Tone> = {
  REGISTERED: 'info',
  ASSIGNED: 'success',
  UNASSIGNED: 'neutral',
  MOVED: 'neutral',
  CONDITION_CHANGED: 'neutral',
  MAINTENANCE_STARTED: 'warning',
  MAINTENANCE_COMPLETED: 'success',
  REPORTED_LOST: 'danger',
  FOUND: 'success',
  DISPOSED: 'neutral',
  AUDITED: 'info',
};

export const STOCK_MOVEMENT_LABEL: Record<StockMovementType, string> = {
  RECEIVE: 'Received',
  ISSUE: 'Issued',
  ADJUST: 'Adjusted',
};

export const LOCATION_KIND_LABEL: Record<LocationKind, string> = {
  CLASSROOM: 'Classroom',
  LABORATORY: 'Laboratory',
  OFFICE: 'Office',
  STORE: 'Store',
  SPORTS: 'Sports',
  LIBRARY: 'Library',
  OTHER: 'Other',
};
